import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DndContext, PointerSensor, useSensor, useSensors, useDroppable, pointerWithin, type DragEndEvent } from '@dnd-kit/core';
import { getDayLabel, isToday, formatDateKey, getProjectleiderColor } from '@/utils/planning';
import type { ProjectleiderGroup, InstallerRow } from '@/utils/planning';
import { PlanningCard } from './PlanningCard';
import { InstallerToggleDropdown } from '@/components/ui/InstallerToggleDropdown';
import { CellContextMenu } from './CellContextMenu';
import { usePlanningStore } from '@/stores/planning';
import { usePermissionsStore } from '@/stores/permissions';
import { moveInstallerInOrder } from '@/utils/planningRowOrder';
import { MultiWeekHeaderRow, StickyScrollbar, useFocusWeekScroll } from './MultiWeekBoard';
import { PlanningZoomContext } from './PlanningZoom';
import type { OdooTask } from '@/types/odoo';

/** A draggable item that originates OUTSIDE the grid (the dashboard Inplannen tray),
 *  dropped onto a grid cell. */
export type TrayDropPayload =
  | { kind: 'unplanned-task'; taskId: number }
  | { kind: 'archived-task'; taskId: number }
  | { kind: 'fase'; faseId: number };

interface PlanningGridProps {
  groups?: ProjectleiderGroup[];
  flatRows?: InstallerRow[];
  days: Date[];
  feestdagDates?: Map<string, import('@/utils/planning').FeestdagInfo>;
  collapsedGroups: Set<number>;
  onToggleCollapse: (id: number) => void;
  /** When set, ONLY cards belonging to this project are editable/draggable; all
   *  other projects render as read-only context (dashboard Inplannen modal). */
  scopeProjectId?: number;
  /** Allow installer row drag-to-reorder (default true; the modal disables it so it
   *  can't mutate the saved board preferences). */
  allowRowReorder?: boolean;
  /** Suppress cell click → context menu / create-schedule transient modes
   *  (default false; the modal is drag-first). */
  disableCellInteractions?: boolean;
  /** Extra content rendered alongside the grid INSIDE the shared DnD context, so its
   *  draggables can drop onto grid cells (the Inplannen tray). */
  renderAside?: React.ReactNode;
  /** Called when a tray draggable (see TrayDropPayload) is dropped on a cell. */
  onTrayDrop?: (payload: TrayDropPayload, target: { dateKey: string; installerId: number }) => void;
  /** Clicking a card body opens its detail/description popup (dashboard scheduler). */
  onCardClick?: (task: OdooTask) => void;
  /** Fires after a card is rescheduled (moveTask resolves) so the host can refresh. */
  onTaskMoved?: () => void;
  /** Multi-week board: the selected week's Monday. The board scrolls to it and
   *  shows a week label row plus week dividers. Unset = single-week layout. */
  focusWeekKey?: string;
}

interface SwimLane {
  laneKey: number;
  dayCells: Map<string, OdooTask[]>;
}

/**
 * Build swimming lanes for an installer row using project-level bin-packing.
 * All tasks from the same project stay on one lane (stacked in the cell).
 * Non-overlapping projects share a lane to minimise vertical space.
 */
function buildSwimLanes(row: InstallerRow, days: Date[]): SwimLane[] {
  const projectGroups = new Map<number, { dates: Set<string>; tasksByDate: Map<string, OdooTask[]> }>();

  for (const day of days) {
    const dateKey = formatDateKey(day);
    for (const task of row.cells.get(dateKey) ?? []) {
      const pid = task.project_id ? task.project_id[0] : 0;
      let group = projectGroups.get(pid);
      if (!group) {
        group = { dates: new Set(), tasksByDate: new Map() };
        projectGroups.set(pid, group);
      }
      group.dates.add(dateKey);
      const existing = group.tasksByDate.get(dateKey);
      if (existing) existing.push(task);
      else group.tasksByDate.set(dateKey, [task]);
    }
  }

  if (projectGroups.size === 0) return [];

  const lanes: { id: number; dates: Set<string>; cells: Map<string, OdooTask[]> }[] = [];
  let nextId = 0;

  for (const [, projData] of projectGroups) {
    let target = lanes.find((l) => ![...projData.dates].some((d) => l.dates.has(d)));

    if (target) {
      for (const d of projData.dates) target.dates.add(d);
      for (const [dk, tasks] of projData.tasksByDate) target.cells.set(dk, tasks);
    } else {
      const cells = new Map<string, OdooTask[]>();
      for (const [dk, tasks] of projData.tasksByDate) cells.set(dk, tasks);
      lanes.push({ id: nextId++, dates: new Set(projData.dates), cells });
    }
  }

  return lanes.map((l) => ({ laneKey: l.id, dayCells: l.cells }));
}

let justClosedInstallerEditor = false;
let droppableCellCounter = 0;

function DroppableCell({ dateKey, installerId, children, className, onClick }: {
  dateKey: string;
  installerId: number;
  children?: React.ReactNode;
  className: string;
  onClick?: (e: React.MouseEvent) => void;
}) {
  // Use a unique suffix to avoid duplicate IDs when same installer appears in multiple PL groups
  const [uniqueId] = useState(() => `${dateKey}:${installerId}:${droppableCellCounter++}`);
  const { setNodeRef, isOver } = useDroppable({ id: uniqueId, data: { dateKey, installerId } });
  const isActiveContextCell = usePlanningStore(
    (s) => (s.cellContextMenu?.dateKey === dateKey && s.cellContextMenu?.installerId === installerId)
      || (s.scheduleTargetCell?.dateKey === dateKey && s.scheduleTargetCell?.installerId === installerId),
  );
  return (
    <td
      ref={setNodeRef}
      onClick={onClick}
      className={`${className} ${
        isOver ? '!bg-teal-100 ring-2 ring-inset ring-teal-400'
        : isActiveContextCell ? '!bg-td-blue/15 ring-2 ring-inset ring-td-blue'
        : ''
      }`}
    >
      {children}
    </td>
  );
}

export function PlanningGrid({
  groups,
  flatRows,
  days,
  feestdagDates,
  collapsedGroups,
  onToggleCollapse,
  scopeProjectId,
  allowRowReorder = true,
  disableCellInteractions = false,
  renderAside,
  onTrayDrop,
  onCardClick,
  onTaskMoved,
  focusWeekKey,
}: PlanningGridProps) {
  const canPlanningEdit = usePermissionsStore((s) => s.canFeature('planning_edit'));
  const deleteFeestdag = usePlanningStore((s) => s.deleteFeestdag);
  const moveTask = usePlanningStore((s) => s.moveTask);
  const installerRowOrder = usePlanningStore((s) => s.installerRowOrder);
  const setInstallerRowOrder = usePlanningStore((s) => s.setInstallerRowOrder);
  const persistInstallerRowOrder = usePlanningStore((s) => s.persistInstallerRowOrder);
  const tasks = usePlanningStore((s) => s.tasks);
  const userMap = usePlanningStore((s) => s.userMap);
  const categoryMap = usePlanningStore((s) => s.categoryMap);
  const installerEditor = usePlanningStore((s) => s.installerEditor);
  const toggleInstallerInEditor = usePlanningStore((s) => s.toggleInstallerInEditor);
  const closeInstallerEditor = usePlanningStore((s) => s.closeInstallerEditor);

  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!installerEditor) return;
    function onClickOutside(e: MouseEvent) {
      if (editorRef.current && !editorRef.current.contains(e.target as Node)) {
        closeInstallerEditor();
        justClosedInstallerEditor = true;
        requestAnimationFrame(() => { justClosedInstallerEditor = false; });
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [installerEditor, closeInstallerEditor]);
  const dateKeys = useMemo(() => days.map(formatDateKey), [days]);
  const todayKey = useMemo(() => {
    const d = days.find(isToday);
    return d ? formatDateKey(d) : null;
  }, [days]);
  const multiWeek = focusWeekKey != null;
  // Every Monday after the first day starts a new week → left divider.
  const weekStartKeys = useMemo(
    () => (multiWeek ? new Set(days.filter((d, i) => i > 0 && d.getDay() === 1).map(formatDateKey)) : undefined),
    [days, multiWeek],
  );
  // Zoom only applies to the full-page board; the dashboard modal stays at 100%.
  const storeZoom = usePlanningStore((s) => s.zoom);
  const zoom = renderAside ? 1 : storeZoom;
  const scrollRef = useRef<HTMLDivElement>(null);
  // Zoom is part of the window key: a zoom change re-lands on the selected week.
  useFocusWeekScroll(scrollRef, `${dateKeys[0] ?? ''}@${zoom}`, focusWeekKey ?? null, zoom);
  const [draggedInstallerId, setDraggedInstallerId] = useState<number | null>(null);
  const [dragOverInstallerId, setDragOverInstallerId] = useState<number | null>(null);
  const autoScrollDirectionRef = useRef<-1 | 0 | 1>(0);
  const autoScrollFrameRef = useRef<number | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 3 } }));

  function handleDragEnd(event: DragEndEvent) {
    if (!canPlanningEdit) return; // read-only viewers can't move task cards (the card drag source is also gated)
    const { active, over } = event;
    if (!over) return;

    // Read target (droppable) + active (draggable) data.
    const overData = over.data.current as { dateKey?: string; installerId?: number } | undefined;
    const activeData = active.data.current as
      | { taskId?: number; installerId?: number; kind?: TrayDropPayload['kind']; faseId?: number }
      | undefined;

    if (!overData?.dateKey) return;

    const targetDate = overData.dateKey;
    const targetInstallerId = overData.installerId ?? 0;

    // Tray drop (dashboard Inplannen modal): a draggable carrying a `kind`
    // originates outside the grid → hand it to the host instead of moveTask.
    if (activeData?.kind && onTrayDrop) {
      const target = { dateKey: targetDate, installerId: targetInstallerId };
      if (activeData.kind === 'unplanned-task' && typeof activeData.taskId === 'number') {
        onTrayDrop({ kind: 'unplanned-task', taskId: activeData.taskId }, target);
      } else if (activeData.kind === 'archived-task' && typeof activeData.taskId === 'number') {
        onTrayDrop({ kind: 'archived-task', taskId: activeData.taskId }, target);
      } else if (activeData.kind === 'fase' && typeof activeData.faseId === 'number') {
        onTrayDrop({ kind: 'fase', faseId: activeData.faseId }, target);
      }
      return;
    }

    const taskId = activeData?.taskId ?? Number(String(active.id).split(':')[0]);

    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;
    if (task.is_easykit) return; // EasyKit tasks are read-only; belt-and-suspenders against a future regression that re-enables drag.
    // Per-project scope (modal): never move a card outside the scoped project, even
    // if a synthetic drag or a missed `editable` prop made it draggable.
    if (scopeProjectId != null && (task.project_id ? task.project_id[0] : 0) !== scopeProjectId) return;

    const sourceDate = task.date_deadline ? task.date_deadline.slice(0, 10) : '';
    const sourceInstallerId = activeData?.installerId ?? 0;

    if (sourceDate === targetDate && sourceInstallerId === targetInstallerId) return;

    void moveTask(taskId, sourceDate, sourceInstallerId, targetDate, targetInstallerId).then(() => onTaskMoved?.());
  }

  function clearRowDragState() {
    setDraggedInstallerId(null);
    setDragOverInstallerId(null);
    autoScrollDirectionRef.current = 0;
    if (autoScrollFrameRef.current !== null) {
      cancelAnimationFrame(autoScrollFrameRef.current);
      autoScrollFrameRef.current = null;
    }
  }

  function stepAutoScroll() {
    if (autoScrollDirectionRef.current === 0) {
      autoScrollFrameRef.current = null;
      return;
    }

    window.scrollBy({ top: autoScrollDirectionRef.current * 20, left: 0, behavior: 'auto' });
    autoScrollFrameRef.current = requestAnimationFrame(stepAutoScroll);
  }

  function updateAutoScroll(clientY: number) {
    const threshold = 120;
    const viewportHeight = window.innerHeight;
    const nextDirection: -1 | 0 | 1 =
      clientY < threshold ? -1
      : clientY > viewportHeight - threshold ? 1
      : 0;

    autoScrollDirectionRef.current = nextDirection;
    if (nextDirection !== 0 && autoScrollFrameRef.current === null) {
      autoScrollFrameRef.current = requestAnimationFrame(stepAutoScroll);
    }
    if (nextDirection === 0 && autoScrollFrameRef.current !== null) {
      cancelAnimationFrame(autoScrollFrameRef.current);
      autoScrollFrameRef.current = null;
    }
  }

  useEffect(() => {
    return () => {
      if (autoScrollFrameRef.current !== null) {
        cancelAnimationFrame(autoScrollFrameRef.current);
      }
    };
  }, []);

  function handleRowReorder(targetInstallerId: number) {
    if (!canPlanningEdit || draggedInstallerId === null || !flatRows || draggedInstallerId === targetInstallerId) {
      clearRowDragState();
      return;
    }

    // Move against the FULL saved order (preserving installers not on screen this
    // week / hidden by the toggle), not just the visible snapshot — the latter is
    // what caused the order to scramble on week navigation.
    const nextOrder = moveInstallerInOrder(
      installerRowOrder,
      flatRows.map((row) => row.id),
      draggedInstallerId,
      targetInstallerId,
    );
    setInstallerRowOrder(nextOrder);
    void persistInstallerRowOrder(nextOrder);
    clearRowDragState();
  }

  const hasContent = (groups && groups.length > 0) || (flatRows && flatRows.length > 0);

  // Pin the installer column on horizontal scroll so the row label stays visible
  // on narrow / phone screens. Only on the full-page board — the dashboard
  // Inplannen modal (renderAside) keeps its original layout untouched.
  const stickyFirstCol = !renderAside;
  // Solid bg + a right shadow so day cards can never show through / to the left of
  // the pinned installer column on phone (z-20 keeps it above the relative cards).
  const stickyHeadCls = stickyFirstCol ? 'sticky left-0 z-30 bg-gray-50 ' : '';
  const stickyNameCls = stickyFirstCol ? 'sticky left-0 z-20 bg-white shadow-[2px_0_5px_rgba(0,0,0,0.06)] ' : '';

  return (
    <>
      <PlanningZoomContext.Provider value={zoom}>
      <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragEnd={handleDragEnd}>
      {/* Multi-week scrolls sideways: a side margin instead of padding, so no cards
          scroll through a gutter left of the pinned installer column. */}
      <div ref={scrollRef} className={renderAside ? 'flex gap-3 px-2 pb-4 items-start' : `overflow-x-auto overscroll-x-contain ${multiWeek ? 'lg:mx-8 lg:[scrollbar-width:none]' : 'lg:px-8'} pb-24 lg:pb-8`}>
        {renderAside && <aside className="shrink-0 w-64 lg:w-72 self-stretch">{renderAside}</aside>}
        <div className={renderAside ? 'overflow-x-auto min-w-0 flex-1' : 'contents'}>
      {/* Multi-week: 4 weeks × 6 days at ≥200px per day (room for the card title), plus the installer column.
          It uses separate borders: collapsed borders are painted by the table itself,
          which made fast sideways scrolling re-raster ~3× more. Row lines, which
          <tr> borders can't draw in that mode, move onto the cells. */}
      <table className={`w-full ${multiWeek ? 'min-w-[4940px] border-separate border-spacing-0 [&_td]:border-b [&_th]:border-b [&_td]:border-b-gray-200 [&_th]:border-b-gray-200' : 'min-w-[900px] border-collapse'} border border-gray-200 bg-white text-sm table-fixed`}
        // CSS zoom scales cards and text together; w-full still fills the page.
        style={zoom === 1 ? undefined : { zoom }}
      >
        <colgroup>
          <col className="w-[140px]" />
          {days.map((_, i) => (
            <col key={i} />
          ))}
        </colgroup>

        {/* Header */}
        <thead>
          {multiWeek && <MultiWeekHeaderRow days={days} stickyHeadCls={stickyHeadCls} />}
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className={`${stickyHeadCls}px-3 py-2 text-xs font-bold text-td-navy text-left border-r border-gray-200`}>
              Installateur
            </th>
            {days.map((day) => {
              const dk = formatDateKey(day);
              const today = dk === todayKey;
              const feestdagInfo = feestdagDates?.get(dk);
              return (
                <th
                  key={dk}
                  data-date-key={dk}
                  className={`px-2 py-2 text-xs font-bold text-center border-r border-gray-200 last:border-r-0 relative group/header ${weekStartKeys?.has(dk) ? 'border-l-[6px] border-l-gray-300 ' : ''}${
                    feestdagInfo ? 'bg-purple-100 text-purple-700'
                    : today ? 'bg-td-blue/10 text-td-blue'
                    : 'text-td-navy'
                  }`}
                >
                  {getDayLabel(day)}
                  {feestdagInfo && (
                    <>
                      <span className="block text-[10px] font-semibold text-purple-500 mt-0.5">{feestdagInfo.name}</span>
                      <button
                        type="button"
                        onClick={() => deleteFeestdag(feestdagInfo.taskId)}
                        className="absolute top-0.5 right-0.5 p-0.5 rounded text-purple-400 hover:text-red-500 hover:bg-white/80 opacity-0 group-hover/header:opacity-100 transition-opacity"
                        aria-label="Feestdag verwijderen"
                        title="Verwijderen"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {/* PL + Inst mode: grouped by projectleider */}
          {groups && groups.map((group) => {
            const isCollapsed = collapsedGroups.has(group.id);

            return (
              <GroupRows
                key={group.id}
                group={group}
                days={days}
                dateKeys={dateKeys}
                todayKey={todayKey}
                feestdagDates={feestdagDates}
                isCollapsed={isCollapsed}
                onToggle={() => onToggleCollapse(group.id)}
                weekStartKeys={weekStartKeys}
                scopeProjectId={scopeProjectId}
                onTaskMoved={onTaskMoved}
                disableCellInteractions={disableCellInteractions}
                onCardClick={onCardClick}
                stickyNameCls={stickyNameCls}
              />
            );
          })}

          {/* Flat installer mode: no PL headers */}
          {flatRows && flatRows.map((row) => (
            <InstallerLanes
              key={row.id}
              row={row}
              days={days}
              dateKeys={dateKeys}
              todayKey={todayKey}
              feestdagDates={feestdagDates}
              compact
              weekStartKeys={weekStartKeys}
              scopeProjectId={scopeProjectId}
              onTaskMoved={onTaskMoved}
              disableCellInteractions={disableCellInteractions}
              onCardClick={onCardClick}
              stickyNameCls={stickyNameCls}
              // Drag-to-reorder is an editor-only affordance. Read-only viewers
              // (planning:read without planning:write) get no rowDragState, so the
              // grip is hidden and the 403-on-PUT path can't be triggered.
              rowDragState={(allowRowReorder && canPlanningEdit) ? {
                isDragging: draggedInstallerId === row.id,
                isDragOver: dragOverInstallerId === row.id && draggedInstallerId !== row.id,
                onDragStart: (clientY) => {
                  setDraggedInstallerId(row.id);
                  updateAutoScroll(clientY);
                },
                onDragOver: (clientY) => {
                  setDragOverInstallerId(row.id);
                  updateAutoScroll(clientY);
                },
                onDrop: () => handleRowReorder(row.id),
                onDragEnd: clearRowDragState,
              } : undefined}
            />
          ))}

          {/* Empty state */}
          {!hasContent && (
            <tr>
              <td
                colSpan={days.length + 1}
                className="py-12 text-center text-td-muted"
              >
                {multiWeek ? 'Geen taken voor deze weken' : 'Geen taken voor deze week'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
        </div>
      </div>
      {multiWeek && <StickyScrollbar targetRef={scrollRef} />}
      </DndContext>
      </PlanningZoomContext.Provider>

      <CellContextMenu />

      {installerEditor && createPortal(
        <div ref={editorRef}>
          <InstallerToggleDropdown
            userMap={userMap}
            categoryMap={categoryMap}
            selectedIds={installerEditor.selectedIds}
            onToggle={toggleInstallerInEditor}
            onClose={closeInstallerEditor}
            projectName={installerEditor.projectName}
            fixedPos={installerEditor.pos}
          />
        </div>,
        document.body,
      )}
    </>
  );
}

interface GroupRowsProps {
  group: ProjectleiderGroup;
  days: Date[];
  dateKeys: string[];
  todayKey: string | null;
  feestdagDates?: Map<string, import('@/utils/planning').FeestdagInfo>;
  isCollapsed: boolean;
  onToggle: () => void;
  scopeProjectId?: number;
  onTaskMoved?: () => void;
  disableCellInteractions?: boolean;
  onCardClick?: (task: OdooTask) => void;
  stickyNameCls?: string;
  weekStartKeys?: Set<string>;
}

function GroupRows({ group, days, dateKeys, todayKey, feestdagDates, isCollapsed, onToggle, scopeProjectId, onTaskMoved, disableCellInteractions, onCardClick, stickyNameCls, weekStartKeys }: GroupRowsProps) {
  return (
    <>
      {/* PL header */}
      <tr
        className="bg-gray-100 border-b border-gray-200 cursor-pointer hover:bg-gray-150"
        onClick={onToggle}
      >
        <td colSpan={days.length + 1} className="px-3 py-2">
          {/* Multi-week: the row is thousands of px wide and opens scrolled right,
              so pin the label to the left edge like the installer names. */}
          <div className={weekStartKeys ? 'sticky left-3 inline-flex items-center gap-2' : 'flex items-center gap-2'}>
            <span className="w-3 h-3 rounded-full bg-td-blue shrink-0" />
            <span className="font-bold text-td-navy">{group.name}</span>
            <span className="text-xs text-td-muted">({group.installerRows.length})</span>
            <svg
              className={`w-4 h-4 text-td-muted ml-auto transition-transform ${
                isCollapsed ? '-rotate-90' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </td>
      </tr>

      {/* Installer rows with swimming lanes */}
      {!isCollapsed &&
        group.installerRows.map((row) => (
          <InstallerLanes
            key={row.id}
            row={row}
            days={days}
            dateKeys={dateKeys}
            todayKey={todayKey}
            feestdagDates={feestdagDates}
            groupPlId={group.id}
            weekStartKeys={weekStartKeys}
            scopeProjectId={scopeProjectId}
            onTaskMoved={onTaskMoved}
            disableCellInteractions={disableCellInteractions}
            onCardClick={onCardClick}
            stickyNameCls={stickyNameCls}
          />
        ))}
    </>
  );
}

interface InstallerLanesProps {
  row: InstallerRow;
  days: Date[];
  dateKeys: string[];
  todayKey: string | null;
  feestdagDates?: Map<string, import('@/utils/planning').FeestdagInfo>;
  compact?: boolean;
  groupPlId?: number;
  scopeProjectId?: number;
  onTaskMoved?: () => void;
  disableCellInteractions?: boolean;
  onCardClick?: (task: OdooTask) => void;
  stickyNameCls?: string;
  weekStartKeys?: Set<string>;
  rowDragState?: {
    isDragging: boolean;
    isDragOver: boolean;
    onDragStart: (clientY: number) => void;
    onDragOver: (clientY: number) => void;
    onDrop: () => void;
    onDragEnd: () => void;
  };
}

function InstallerLanes({ row, days, dateKeys, todayKey, feestdagDates, compact, groupPlId, rowDragState, scopeProjectId, onTaskMoved, disableCellInteractions, onCardClick, stickyNameCls = '', weekStartKeys }: InstallerLanesProps) {
  const lanes = useMemo(() => buildSwimLanes(row, days), [row, days]);
  const createMode = usePlanningStore((s) => s.createMode);
  const scheduleProjectMode = usePlanningStore((s) => s.scheduleProjectMode);
  const executeCreateTask = usePlanningStore((s) => s.executeCreateTask);
  const executeScheduleProject = usePlanningStore((s) => s.executeScheduleProject);
  const openCellContextMenu = usePlanningStore((s) => s.openCellContextMenu);
  const isAnyModeActive = !!createMode || !!scheduleProjectMode;
  const weekStartCls = (dk: string) => (weekStartKeys?.has(dk) ? ' border-l-[6px] border-l-gray-300' : '');

  function handleCellClick(dk: string, e: React.MouseEvent) {
    if (disableCellInteractions) return; // modal is drag-first: no context menu / mode clicks
    if (justClosedInstallerEditor) return;
    if (createMode) executeCreateTask(dk, row.id);
    else if (scheduleProjectMode) executeScheduleProject(dk, row.id);
    else {
      // The context menu (Project inplannen / aanmaken / Feestdag / Verlof) is an
      // EMPTY-cell action. Ignore clicks that originated on a card inside the cell —
      // cards handle their own tap (→ action sheet on mobile). A card's stopPropagation
      // isn't enough on touch: opening the sheet on pointerup re-renders the card, so
      // the follow-up click retargets to the cell. Gate on the cell being the target.
      if (e.target !== e.currentTarget) return;
      usePlanningStore.setState({ lastClickedFaseId: null });
      openCellContextMenu(dk, row.id, e.clientX, e.clientY);
    }
  }

  if (lanes.length === 0) {
    return (
      <tr
        className={`border-b border-gray-200 ${compact && rowDragState?.isDragging ? 'opacity-60' : ''} ${compact && rowDragState?.isDragOver ? 'bg-teal-50' : ''}`}
        onDragOver={compact && rowDragState ? (event) => { event.preventDefault(); rowDragState.onDragOver(event.clientY); } : undefined}
        onDrop={compact && rowDragState ? (event) => { event.preventDefault(); rowDragState.onDrop(); } : undefined}
      >
        <td className={`${stickyNameCls}px-3 py-2 font-medium text-td-navy border-r border-gray-200 align-top`}>
          {compact && rowDragState && row.id > 0 ? (
            <div className="flex items-start gap-2">
              <button
                type="button"
                draggable
                onDragStart={(event) => { event.dataTransfer.effectAllowed = 'move'; rowDragState.onDragStart(event.clientY); }}
                onDragEnd={() => rowDragState.onDragEnd()}
                className="mt-0.5 shrink-0 cursor-grab rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-td-navy active:cursor-grabbing"
                aria-label={`${row.name} verplaatsen`}
                title="Sleep om volgorde te wijzigen"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 6h.01M8 12h.01M8 18h.01M16 6h.01M16 12h.01M16 18h.01" />
                </svg>
              </button>
              <span className="truncate block">{row.name}</span>
            </div>
          ) : (
            <span className="truncate block">{row.name}</span>
          )}
        </td>
        {dateKeys.map((dk) => (
          <DroppableCell
            key={dk}
            dateKey={dk}
            installerId={row.id}
            onClick={(e) => handleCellClick(dk, e)}
            className={`border-r border-gray-200 last:border-r-0 h-[40px]${weekStartCls(dk)} ${
              feestdagDates?.has(dk) ? 'bg-purple-50'
              : dk === todayKey ? 'bg-td-blue/5'
              : ''
            } ${isAnyModeActive ? 'cursor-pointer hover:bg-teal-50' : 'cursor-pointer'}`}
          />
        ))}
      </tr>
    );
  }

  const rowDragHandlers = compact && rowDragState ? {
    onDragOver: (event: React.DragEvent) => { event.preventDefault(); rowDragState.onDragOver(event.clientY); },
    onDrop: (event: React.DragEvent) => { event.preventDefault(); rowDragState.onDrop(); },
  } : {};

  return (
    <>
      {lanes.map((lane, laneIdx) => (
        <tr
          key={lane.laneKey}
          className={`border-b border-gray-200 last:border-b-0 ${compact && rowDragState?.isDragging ? 'opacity-60' : ''} ${compact && rowDragState?.isDragOver ? 'bg-teal-50' : ''}`}
          {...rowDragHandlers}
        >
          {laneIdx === 0 && (
            <td
              className={`${stickyNameCls}px-3 py-2 font-medium text-td-navy border-r border-gray-200 align-top`}
              rowSpan={lanes.length}
            >
              {compact && rowDragState && row.id > 0 ? (
                <div className="flex items-start gap-2">
                  <button
                    type="button"
                    draggable
                    onDragStart={(event) => { event.dataTransfer.effectAllowed = 'move'; rowDragState.onDragStart(event.clientY); }}
                    onDragEnd={() => rowDragState.onDragEnd()}
                    className="mt-0.5 shrink-0 cursor-grab rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-td-navy active:cursor-grabbing"
                    aria-label={`${row.name} verplaatsen`}
                    title="Sleep om volgorde te wijzigen"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 6h.01M8 12h.01M8 18h.01M16 6h.01M16 12h.01M16 18h.01" />
                    </svg>
                  </button>
                  <span className="truncate block">{row.name}</span>
                </div>
              ) : (
                <span className="truncate block">{row.name}</span>
              )}
            </td>
          )}

          {dateKeys.map((dk) => {
            const tasks = lane.dayCells.get(dk) ?? [];
            return (
              <DroppableCell
                key={dk}
                dateKey={dk}
                installerId={row.id}
                onClick={(e) => handleCellClick(dk, e)}
                className={`p-1 ${laneIdx === lanes.length - 1 ? 'pb-10' : ''} border-r border-gray-200 last:border-r-0${weekStartCls(dk)} ${
                  feestdagDates?.has(dk) ? 'bg-purple-50'
                  : dk === todayKey ? 'bg-td-blue/5'
                  : ''
                } ${isAnyModeActive ? 'cursor-pointer hover:bg-teal-50' : 'cursor-pointer'}`}
              >
                {tasks.map((task) => (
                  <PlanningCard
                    key={task.id}
                    task={task}
                    installerId={row.id}
                    editable={scopeProjectId == null ? undefined : (task.project_id ? task.project_id[0] : 0) === scopeProjectId}
                    onTaskMoved={onTaskMoved}
                    onCardClick={onCardClick}
                    {...(compact ? { colorOverride: getProjectleiderColor(task) } : { groupPlId })}
                  />
                ))}
              </DroppableCell>
            );
          })}
        </tr>
      ))}
    </>
  );
}
